// src/utils/request.ts
import axios, {
    AxiosResponse,
    AxiosError,
    InternalAxiosRequestConfig,
    AxiosRequestConfig
} from 'axios';
import {ElMessage} from "element-plus";
import {useUserStore} from '@/stores/user';
import router from '@/router';

// 全局刷新锁：用于防止并发请求重复刷新token
let refreshPromise: Promise<string | null> | null = null;
// 原生axios实例,刷新token专用
const axiosInstance = axios.create({
    baseURL: '',
    timeout: 10000,
    headers: {'Content-Type': 'application/json;charset=UTF-8'}
});
// 刷新token接口
const refreshTokenApi = async (oldToken: string): Promise<string> => {
    const {data} = await axiosInstance.post(process.env.VUE_APP_API_BASE_URL + '/auth/refresh-token', {token: oldToken})
    if (data.code !== 200) {
        throw new Error('刷新token失败')
    }
    if (data.code == 102004) {
        ElMessage.error("用户已被禁用,请联系管理员");
        const userStore = useUserStore();
        userStore.clearToken();
        setTimeout(() => {
            router.push('/login');
        }, 1000);
    }
    return data.data
};

// 定义后端响应数据的通用接口
interface ApiResponse<T = any> {
    code: number;
    message: string;
    data: T;
}

const service = axios.create({
    baseURL: '',
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json;charset=UTF-8'
    }
});

// 全局刷新token，带刷新锁
const refreshToken = async () => {
    const userStore = useUserStore();
    // 1. 如果没有token/已经在刷新中，直接返回
    if (!userStore.token || refreshPromise) {
        return refreshPromise;
    }
    // 2. 加刷新锁：将刷新请求赋值给全局Promise，所有并发请求都等待这个Promise完成
    refreshPromise = new Promise(async (resolve, reject) => {
        try {
            // 3. 调用刷新接口，获取新token
            const newToken = await refreshTokenApi(userStore.token);
            // 4. 刷新成功：更新pinia和本地存储的token
            userStore.setToken(newToken);
            resolve(newToken);
        } catch (e) {
            // 5. 刷新失败：清除token，返回null
            userStore.clearToken();
            reject(null);
        } finally {
            // 6. 解锁：无论成功失败，都清空刷新锁
            refreshPromise = null;
        }
    });
    return refreshPromise;
};

// 请求拦截器
service.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {

        if (config.params) {
            Object.keys(config.params).forEach(key => {
                if (config.params[key] === null || config.params[key] === undefined) {
                    delete config.params[key];
                }
            });
        }
        // 2. 从 Pinia 获取 Token 并注入请求头
        const userStore = useUserStore(); // 注意：在拦截器内部实例化 Store（避免Pinia未挂载）
        if (userStore.token && config.headers) {
            // 格式按后端要求：常见 Bearer Token / Token xxx
            config.headers.Authorization = `Bearer ${userStore.token}`;
            // 若后端要求 Token 放在 header 的 Token 字段：
            // config.headers['Token'] = userStore.token;
        }
        // 条件：有token + token即将过期 + 不是刷新token的请求（避免循环）
        if (userStore.token && userStore.isTokenWillExpire() && config.url !== '/auth/refresh-token') {
            await refreshToken(); // 等待刷新完成，token已更新
            // 刷新后重新注入新token到请求头
            if (userStore.token && config.headers) {
                config.headers.Authorization = `Bearer ${userStore.token}`;
            }
        }
        return config;
    },
    (error: AxiosError) => {
        console.error('请求错误:', error);
        return Promise.reject(error);
    }
);

// 响应拦截器 - 指定泛型类型
service.interceptors.response.use(
    <T = any>(response: AxiosResponse<ApiResponse<T>>) => {
        if (response.config.responseType === 'blob' ||
            response.headers['content-type']?.includes('application/octet-stream') ||
            response.headers['content-type']?.includes('spreadsheetml.sheet')
        )
        {
            return response; // 直接返回原始响应，不进行JSON解析
        }
        const res = response.data;
        let msg = res.message;
        if (res.code == 401 || res.code == 402) {// 业务逻辑：清 Token + 弹提示 + 跳登录
            const errMsg = res.code == 401 ? '登录状态已过期，请重新登录' : '账号已在其他设备登录';
            ElMessage.error(errMsg);
            const userStore = useUserStore();
            userStore.clearToken();
            setTimeout(() => {
                router.push('/login');
            }, 1000);
        }
        if (res.code == 200) {
            return res.data; // 返回实际数据部分
        }
        if (res.code == 500) {
            console.log("后台服务器异常:", res.message)
            msg = "服务器异常";
        }
        return Promise.reject(new Error(msg || '请求失败'));
    },
    (error: AxiosError<{ message: string }>) => { // 指定错误响应类型
        let message = '请求失败';

        if (error.response) {
            // 明确指定response的类型
            const {status, data} = error.response;
            message = `HTTP错误 ${status}: ${data.message || '服务器错误'}`;
        } else if (error.request) {
            message = '请求超时，请重试';
        } else {
            message = error.message || '请求配置错误';
        }

        ElMessage({message, type: 'error', duration: 3000});
        return Promise.reject(error);
    }
);

// 封装请求方法
const request = {
    get<T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> {
        return service.get(url, {params, ...config});
    },

    post<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
        return service.post(url, data, config);
    },

    put<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
        return service.put(url, data, config);
    },

    delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return service.delete(url, config);
    },

    upload<T = any>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
        return service.post(url, formData, {
            ...config,
            headers: {'Content-Type': 'multipart/form-data'}
        });
    },

    download(url: string, params?: any, config?: AxiosRequestConfig): Promise<Blob> {
        return params
            ? service.post(url, params, {...config, responseType: 'blob'})
            : service.get(url, {...config, responseType: 'blob', params: params});
    },

    withCancel<T = any>(config: AxiosRequestConfig) {
        const source = axios.CancelToken.source();
        const request = service.request<T>({
            ...config,
            cancelToken: source.token
        });

        return {
            request,
            cancel: (message?: string) => source.cancel(message || '请求被取消')
        };
    }
};

export default request;
